import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { AppErrorFallback } from '@wapharma/design-system';
import {
  buildReportIssueContext,
  getLastTraceId,
  readReproBreadcrumbs,
  setLastSentryEventId,
} from '@wapharma/shared';
import { API_BASE } from '../lib/apiBase';
import { getToken } from '../auth';

type Props = {
  children: ReactNode;
};

type State = {
  hasError: boolean;
  sentryEventId: string | null;
  lastErrorMessage: string | null;
};

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, sentryEventId: null, lastErrorMessage: null };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const eventId = Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
    if (eventId) {
      setLastSentryEventId(eventId);
      this.setState({ sentryEventId: eventId });
    }
    this.setState({ lastErrorMessage: `${error.name}: ${error.message}` });

    // Auto-ticket (best effort). Dedupe by Sentry event id in this tab.
    try {
      const key = eventId ? `wapharma_auto_ticket:${eventId}` : null;
      if (key && sessionStorage.getItem(key) === '1') return;
      if (key) sessionStorage.setItem(key, '1');

      const traceId = getLastTraceId();
      const token = getToken();
      const ctx = buildReportIssueContext({
        surface: 'admin',
        route: window.location.pathname + window.location.search,
        componentStack: errorInfo.componentStack ?? undefined,
      });
      const repro = readReproBreadcrumbs();

      void fetch(`${API_BASE}/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          type: 'BUG',
          subject: `Crash admin ${window.location.pathname}`,
          description: `${error.name}: ${error.message}\n\n${errorInfo.componentStack ?? ''}`,
          priority: 'HIGH',
          context: { ...ctx, repro, traceId, sentryEventId: eventId },
        }),
      });
    } catch {
      // ignore
    }
  }

  private handleRetry = () => window.location.reload();

  private handleGoHome = () => {
    window.location.assign('/');
  };

  render() {
    if (this.state.hasError) {
      const msg = String(this.state.lastErrorMessage ?? '');
      const isRecoverableChunk =
        /chunkloaderror/i.test(msg) ||
        /loading chunk/i.test(msg) ||
        /failed to fetch dynamically imported module/i.test(msg);
      return (
        <AppErrorFallback
          traceId={getLastTraceId()}
          onGoHome={this.handleGoHome}
          onRetry={isRecoverableChunk ? this.handleRetry : undefined}
        />
      );
    }
    return this.props.children;
  }
}
